import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  createSearchParams,
  Outlet,
  Link,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client';

import {
  Button,
  Container,
  Headline,
  Icon,
  IconButton,
  IconBlock,
  ErrorMessage,
} from 'anf-core-react';

import { GET_TMNTS } from '../../gql/tmnts.gql';
import GET_ESTIMATE_CALCULATION from '../../gql/order-calculation.gql';
import GET_ORIGINAL_ORDER from '../../gql/get-original-order.gql';

import useSessionStorage from '../../hooks/use-session-storage';

import TmntContext from '../../context/tmnt';

import Loading from '../../components/loading/loading';
import ErrorDisplay from '../../components/error-display/error-display';
import FinalizeExchange from '../../components/finalize-exchange/finalize-exchange';
import TmntText from '../../components/tmnt-text/tmnt-text';
import ExchangeProgress from '../../components/exchange-progress/exchange-progress';
import mapProductReturnDetail from '../../utils/map-product-return-detail';
import trackAction from '../../utils/trackAction';
import { getParams } from '../../utils/params';

export default function ExchangeView() {
  // Fetch the orderId from the URL
  const { orderId, storeId } = useParams();

  // Ready controls the state of the continue button below.  Child pages enable and disable
  // the continue button through this state variable.
  const [ready, setReady] = useState(false);
  const [invalidReturnReasons, setInvalidReturnReasons] = useState(false);
  const [showInvalidReturnReasons, setShowInvalidReturnReasons] = useState(false);

  // Selected Products are a list of products that have been selected by the user to be returned.
  const [selectedProducts, setSelectedProducts, selectedProductsLoaded] = useSessionStorage(`${orderId}-p`, []);

  // Selected Product Comments are a list of the users reasons for returning the selected product.
  const [selectedProductComments, setSelectedProductComments] = useSessionStorage(`${orderId}-c`, {});

  // Child views will set the selected shipping method,
  // seleted shipping method and refund method for this order
  const [selectedShippingMethod, setSelectedShippingMethod] = useSessionStorage(`${orderId}-s`, {});
  const [legacySelectedRefundMethod, setLegacySelectedRefundMethod] = useSessionStorage(`${orderId}-r`, '');
  const [selectedRefundMethod, setSelectedRefundMethod] = useSessionStorage(`${orderId}-rm`, null);

  // Manage the loading indicator on the continue button when browsing between pages
  const [isProcessing, setIsProcessing] = useState(true);

  // Child views will also set the title of the page
  const [pageTitle, setPageTitle] = useState('');

  const [currentPostalCode, setCurrentPostalCode] = useState('');

  const [carriers, setCarriers] = useState();

  const [postalCodeSet, setPostalCodeSet] = useState(false);

  const [refundMethods, setRefundMethods] = useState();
  const [returnDetails, setReturnDetails] = useState();

  const location = useLocation();
  const navigate = useNavigate();
  const viewportRef = useRef();
  const { email } = getParams(global.window);

  // Here is where all possible steps in the flow are defined as links
  const flow = useMemo(
    () => ([
      { path: `/store/${storeId}/exchange/${orderId}` },
      { path: `/store/${storeId}/exchange/${orderId}/location` },
      { path: `/store/${storeId}/exchange/${orderId}/refund-method` },
      { path: `/store/${storeId}/exchange/${orderId}/review`, finalize: true },
      { path: `/store/${storeId}/exchange/${orderId}/confirmation` },
    ]),
    [storeId, orderId],
  );

  // Get the detail for the individual order the user is interacting with
  const {
    loading: loadingDetail,
    error: errorDetail,
    data: order,
  } = useQuery(GET_ORIGINAL_ORDER, {
    variables: {
      orderId,
      email,
    },
  });

  // Get the return orders estimated refund amount
  const { data: tmnts } = useQuery(GET_TMNTS);
  const [fetchCalculation, { data: calculation }] = useLazyQuery(GET_ESTIMATE_CALCULATION);

  const hasHazmatItem = useMemo(
    () => {
      const tempAreItemsHazmat = selectedProducts.some((product) => product.isHazmat);
      return tempAreItemsHazmat;
    },
    [selectedProducts],
  );

  const selectedRefundMethodType = useMemo(
    () => {
      if (selectedRefundMethod) {
        return selectedRefundMethod.id;
      }
      return legacySelectedRefundMethod;
    },
    [selectedRefundMethod, legacySelectedRefundMethod],
  );

  const estimatedRefundCalculation = useMemo(
    () => {
      if (selectedRefundMethod) {
        // selectedRefundMethod is the refund method object that has the orderCalculation
        return selectedRefundMethod;
      }
      return calculation;
    },
    [calculation, selectedRefundMethod],
  );

  // Boolean - Does this return order include exchange items
  const hasExchange = useMemo(
    () => !!selectedProducts.filter((p) => !!p.exchange).length,
    [selectedProducts],
  );

  const waiveReturnFee = useMemo(
    () => {
      if (selectedRefundMethod) {
        return selectedRefundMethod?.returnFeeWaived;
      }
      return null;
    },
    [selectedRefundMethod],
  );

  // Boolean - Does this return order include return items
  const hasReturn = useMemo(
    () => !!selectedProducts.filter((p) => !p.exchange).length,
    [selectedProducts],
  );

  // Exchange only orders will skip the payment method selection
  const activeFlow = useMemo(
    () => ((hasExchange && !hasReturn) ? [flow[1], flow[3]] : [flow[1], flow[2], flow[3]]),
    [flow, hasExchange, hasReturn],
  );

  // Reference to the current step if it is part of the activeFlow
  const activeStep = useMemo(
    () => activeFlow.find((i) => i.path === location.pathname),
    [activeFlow, location],
  );

  const viewingDropOffLocations = useMemo(
    () => location.pathname.indexOf('drop-off-locations') !== -1,
    [location],
  );

  const navigateToCompleteReturn = useCallback(
    (data) => {
      navigate({
        pathname: flow[4].path,
        search: createSearchParams({
          exchange: Number(!!hasExchange),
          return: Number(!!hasReturn),
          label: data.orderReturn.returnOrderId,
          // NOTE: passing the type rather than the id. type is a combination of the carrier
          // name and the returnMethodPreference
          shippingMethod: selectedShippingMethod.type,
          refundMethod: selectedRefundMethodType,
          hazmat: Number(hasHazmatItem),
        }).toString(),
      });
    },
    [
      flow,
      hasExchange,
      hasReturn,
      selectedShippingMethod,
      selectedRefundMethodType,
      selectedProducts,
      hasHazmatItem,
    ],
  );

  // Ask crs-app to scroll to the top of the iframe
  useEffect(() => {
    setIsProcessing(false);

    // Always Scroll To Window Top
    window.scrollTo(0, 0);
  }, [location]);

  // Always redirect the user to the first step when they navigate and no products are selected
  useEffect(() => {
    if (!selectedProductsLoaded) return;
    if (flow[0].path === location.pathname) return;

    if (!selectedProducts?.length) {
      navigate(flow[0].path);
    }
  }, [location, flow, selectedProducts, selectedProductsLoaded]);

  useEffect(() => {
    if (postalCodeSet) return;
    if (!order) return;
    const { orderDetails } = order.originalOrder;
    setCurrentPostalCode(orderDetails?.shippingAddress?.postalCode);
    setPostalCodeSet(true);
  }, [order, postalCodeSet]);

  useEffect(() => {
    if (!selectedProducts.length || !ready || invalidReturnReasons) {
      return;
    }

    const { orderDetails } = order.originalOrder;
    let payments;

    if (orderDetails.payments) {
      payments = orderDetails.payments.map((payment, idx) => ({
        amount: orderDetails.totalPaidPriceFmt.replace(/[^0-9.,]/g, ''),
        paymentName: payment.paymentName,
        sequence: idx.toString(),
      }));
    } else {
      payments = [{
        amount: '0.00',
        paymentName: 'GIFT_CARD',
        sequence: '0',
      }];
    }

    const payload = {
      orderId,
      shipToCountryCode: orderDetails.shippingAddress.country,
      payments,
      returnFeeWaived: orderDetails.returnFeeWaived.toString(),
      returnItems: selectedProducts.map((product, idx) => {
        const comments = selectedProductComments[product.orderLineKey];
        return mapProductReturnDetail({ product, comments, seq: idx });
      }),
      returnShippingFee: orderDetails.returnShippingFee,
    };

    if (payload.returnItems.some((item) => !(item.returnReasonCode || item.returnReasons))) {
      return;
    }

    if (hasReturn) {
      // return only scenario
      payload.refundTenderType = orderDetails.refundTenderType;
      setReturnDetails(payload);
      setRefundMethods(null);
      setSelectedRefundMethod(null);
    } else {
      fetchCalculation({
        variables: {
          returnDetails: payload,
        },
      });
    }
  }, [
    selectedProducts,
    selectedProductComments,
    orderId,
    order,
    ready,
    hasReturn,
    invalidReturnReasons,
  ]);

  const headlineContent = useMemo(() => {
    if (!order || !pageTitle) return null;

    return (
      <Headline
        align="center"
        theme="default"
      >
        <TmntText k={pageTitle} />
      </Headline>
    );
  }, [pageTitle, order]);

  useEffect(() => {
    // don't bind event handler if we don't have data
    if (!order) return () => { };

    // trigger tealium event once process initiated
    const trackData = {
      page_type: 'my account',
      page_description: 'start this exchange/refund',
      exchange_quantity: 0,
      return_quantity: 0,
      return_method: null,
      return_refund_method: null,
    };
    trackAction('return_exchange', trackData);

    const updateExchangeStatus = (event) => {
      const {
        lineItemId: orderLineKey,
        returnReasonDetailCode,
        reasonName,
      } = event.detail;

      let {
        returnReasonComments,
      } = event.detail;

      if (typeof returnReasonComments?.value === 'string') {
        returnReasonComments = returnReasonComments?.value;
      }

      setSelectedProducts((currentProducts) => (
        currentProducts.reduce((products, product) => (
          [
            ...products,
            product.orderLineKey === orderLineKey ? {
              ...product,
              exchange: {
                ...event.detail,
              },
            } : product,
          ]
        ), [])
      ));

      setSelectedProductComments((currentProductComments) => {
        const secondaryReasonCode = returnReasonDetailCode
          ? parseInt(returnReasonDetailCode, 10) : null;

        const productComment = currentProductComments[orderLineKey];
        const exchangeReason = order.returnReasonCodeList.find(
          (reasonCode) => reasonCode.value === reasonName,
        );
        const exchangeSecondaryReason = exchangeReason?.options?.find(
          (option) => option.id === secondaryReasonCode,
        );

        const additionalComments = (
          returnReasonComments && returnReasonComments !== exchangeSecondaryReason?.label
        ) ? returnReasonComments : productComment?.exchangeComment || '';

        return {
          ...currentProductComments,
          [orderLineKey]: {
            ...productComment,
            exchangeReason,
            exchangeSecondaryReason,
            exchangeComment: additionalComments,
          },
        };
      });
    };

    window.addEventListener('dre:updateExchangeStatus', updateExchangeStatus);

    return () => {
      window.removeEventListener('dre:updateExchangeStatus', updateExchangeStatus);
    };
  }, [order]);

  const mapApiKey = useMemo(() => order?.googleApiKey, [order]);

  const continueBtn = useMemo(
    () => {
      const currentIdx = activeFlow.indexOf(activeStep);
      const trackReturnDataTitle = [
        'return for a refund',
        'return method',
        'return refund method',
        'return submit',
      ];
      const trackExchangeDataTitle = [
        'exchange',
        'exchange method',
        'exchange refund method',
        'exchange submit',
      ];
      const exchangeQuantity = selectedProducts.filter((p) => !!p.exchange).length;
      const returnQuantity = selectedProducts.filter((p) => !p.exchange).length;
      const pageDescription = returnQuantity > 0 ? trackReturnDataTitle[currentIdx + 1]
        : trackExchangeDataTitle[currentIdx + 1];

      let isDisabled = !ready || isProcessing;

      if (pageDescription === 'return refund method' && !selectedRefundMethodType) {
        isDisabled = true;
      }
      const button = (
        <Button
          variant="secondary"
          isDisabled={isDisabled}
          isProcessing={isProcessing}
          onClick={() => {
            if (!invalidReturnReasons) {
              setIsProcessing(true);
              const trackData = {
                page_type: 'my account',
                page_description: pageDescription,
                exchange_quantity: exchangeQuantity,
                return_quantity: returnQuantity,
                return_method: selectedShippingMethod?.name || null,
                return_refund_method: selectedRefundMethod?.trackName,
              };
              trackAction('return_exchange', trackData);
            } else {
              setShowInvalidReturnReasons(true);
            }
          }}
        >
          <TmntText k="RNE_RETURNSFLOW_CONTINUE" />
        </Button>
      );

      return invalidReturnReasons
        ? (
          <>
            {showInvalidReturnReasons && (
              <div className="return-error-message">
                <ErrorMessage>
                  <TmntText k="DRE_MAKE_SELECTION_TO_CONTINUE" />
                </ErrorMessage>
              </div>
            )}
            {button}
          </>
        )
        : (
          <Link to={activeFlow[currentIdx + 1]?.path}>
            {button}
          </Link>
        );
    },
    [
      activeFlow,
      activeStep,
      ready,
      isProcessing,
      setIsProcessing,
      selectedShippingMethod,
      selectedRefundMethodType,
      selectedProducts,
      selectedRefundMethod,
      invalidReturnReasons,
      showInvalidReturnReasons,
      setShowInvalidReturnReasons,
    ],
  );

  const iconHeaderText = TmntText({ k: 'EXCHANGES_HEADER_RETURNEXCHANGEITEMS', textOnly: true });

  if (loadingDetail || (!errorDetail && !order)) {
    return <Loading />;
  }

  if (errorDetail) return <ErrorDisplay apolloError={errorDetail} />;

  return (
    <TmntContext.Provider value={tmnts}>
      <div className="exchange-view" ref={viewportRef}>
        <Container
          backgroundColor="primary-light"
          orientation="vertical"
          spacing="s"
          textAlign="left"
        >
          <div className="exchange-view-section">
            <div className="exchange-view-top">
              <div className="exchange-view-top-wrapper">
                <div className="exchange-view-header">
                  <IconButton
                    onClick={() => navigate(-1)}
                    icon="left-large"
                    size="m"
                    data-testid="navigate-left"
                    theme="inverse"
                    ariaLabel={iconHeaderText}
                  />
                  <IconBlock icon={<Icon icon="returns" size="m" />} orientation="horizontal">
                    <TmntText k="EXCHANGES_HEADER_RETURNEXCHANGEITEMS" className="header-text text-bold" />
                  </IconBlock>
                </div>
                {!viewingDropOffLocations && headlineContent}

                {!viewingDropOffLocations && activeStep && (
                  <ExchangeProgress
                    steps={activeFlow}
                    activeStep={activeStep}
                    navigate={navigate}
                  />
                )}
              </div>
              <div className="outlet-wrapper">
                <Outlet context={{
                  calculation,
                  estimatedRefundCalculation,
                  flow,
                  orderDetail: {
                    originalOrder: order.originalOrder,
                    returnReasonCodeList: order.returnReasonCodeList,
                    returnReasons: order.returnReasons,
                  },
                  setPageTitle,
                  setReady,
                  setInvalidReturnReasons,
                  selectedProducts,
                  selectedProductComments,
                  selectedShippingMethod,
                  legacySelectedRefundMethod,
                  selectedRefundMethodType,
                  setSelectedProducts,
                  setSelectedProductComments,
                  setSelectedShippingMethod,
                  setLegacySelectedRefundMethod,
                  selectedRefundMethod,
                  setSelectedRefundMethod,
                  currentPostalCode,
                  setCurrentPostalCode,
                  carriers,
                  setCarriers,
                  mapApiKey,
                  returnDetails,
                  setReturnDetails,
                  refundMethods,
                  setRefundMethods,
                  invalidReturnReasons,
                  showInvalidReturnReasons,
                  setShowInvalidReturnReasons,
                }}
                />
              </div>
            </div>
            <div className="exchange-view-bottom">
              {activeStep?.finalize && (
                <FinalizeExchange
                  calculation={estimatedRefundCalculation}
                  selectedProducts={selectedProducts}
                  selectedProductComments={selectedProductComments}
                  legacySelectedRefundMethod={legacySelectedRefundMethod}
                  selectedRefundMethod={selectedRefundMethod}
                  selectedShippingMethod={selectedShippingMethod}
                  orderDetail={{ originalOrder: order.originalOrder }}
                  next={navigateToCompleteReturn}
                  waiveReturnFee={waiveReturnFee}
                  trackName={selectedRefundMethod?.trackName}
                />
              )}
              {!viewingDropOffLocations && !activeStep?.finalize && continueBtn}
              {!!viewingDropOffLocations && (
                <Button
                  variant="secondary"
                  onClick={() => navigate(-1)}
                >
                  <TmntText k="DRE_BACK_TO_RETURN_METHOD" />
                </Button>
              )}
            </div>
          </div>
        </Container>
      </div>
    </TmntContext.Provider>
  );
}
